import type { BreakRule, ShiftHours } from "@/shared/models/BreakRule";

export type BreakRuleForm = {
	AssetId: number | null;
	ShiftHours: ShiftHours;
	Label: string;
	OffsetMinutes: string;
	DurationMinutes: string;
	IsSpell: boolean;
};

export const WHOLE_MINUTES = /^\d+$/;

export const toForm = (rule: BreakRule | null): BreakRuleForm => ({
	AssetId: rule?.AssetId ?? null,
	ShiftHours: rule?.ShiftHours ?? 8,
	Label: rule?.Label ?? "",
	OffsetMinutes: rule?.OffsetMinutes != null ? String(rule.OffsetMinutes) : "",
	DurationMinutes:
		rule?.DurationMinutes != null ? String(rule.DurationMinutes) : "",
	IsSpell: rule?.IsSpell ?? false,
});

export const toRule = (
	form: BreakRuleForm,
	breakRuleId: number | null,
): BreakRule => ({
	...form,
	BreakRuleId: breakRuleId,
	Label: form.Label.trim(),
	OffsetMinutes: Number(form.OffsetMinutes),
	DurationMinutes: Number(form.DurationMinutes),
});

export const siblingRules = (
	rules: BreakRule[],
	form: BreakRuleForm,
	editingRuleId: number | null,
) =>
	rules.filter(
		(r) =>
			r.AssetId === form.AssetId &&
			r.ShiftHours === form.ShiftHours &&
			r.BreakRuleId !== editingRuleId,
	);

export const validateLabel = (label: string, siblings: BreakRule[]) => {
	if (!label.trim()) return "Label is required";
	if (label.trim().length > 30) return "Label must be 30 characters or fewer";
	return (
		!siblings.some(
			(r) => r.Label.trim().toLowerCase() === label.trim().toLowerCase(),
		) || "Another break of this line and shift length has this label"
	);
};

export const validateOffset = (value: string, hours: ShiftHours) => {
	if (!value) return "Start is required";
	if (!WHOLE_MINUTES.test(value)) return "Whole minutes only (0 or more)";
	return (
		Number(value) < hours * 60 ||
		`Must start inside the shift (0–${hours * 60 - 1})`
	);
};

export const validateDuration = (
	value: string,
	form: BreakRuleForm,
	siblings: BreakRule[],
) => {
	if (!value) return "Duration is required";
	if (!WHOLE_MINUTES.test(value) || Number(value) < 1)
		return "Whole minutes only (1 or more)";
	if (validateOffset(form.OffsetMinutes, form.ShiftHours) !== true) return true;
	const start = Number(form.OffsetMinutes);
	const end = start + Number(value);
	if (end > form.ShiftHours * 60) return "Runs past the end of the shift";
	const hit = siblings.find(
		(r) =>
			r.OffsetMinutes != null &&
			r.DurationMinutes != null &&
			start < r.OffsetMinutes + r.DurationMinutes &&
			r.OffsetMinutes < end,
	);
	return hit
		? `Overlaps ${hit.Label} (${hit.OffsetMinutes}–${(hit.OffsetMinutes ?? 0) + (hit.DurationMinutes ?? 0)})`
		: true;
};
